"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.INCENTIVE_PERCENTILES = exports.SCORING_STATUSES = exports.COMPLETENESS_BASES = exports.CASE_MINIMUM_BASES = exports.PARTIAL_POINTS_METHODS = exports.SCORING_RULESETS = void 0;
exports.rates = rates;
exports.dataCompleteness = dataCompleteness;
exports.findDecile = findDecile;
exports.decilePoints = decilePoints;
exports.scoreMeasure = scoreMeasure;
exports.qualityProjection = qualityProjection;
exports.decileAtPercentile = decileAtPercentile;
exports.decileBoundary = decileBoundary;
exports.reachesDecile = reachesDecile;
exports.reportingIncentive = reportingIncentive;
exports.standardFloor = standardFloor;
// Decile, points and quality-score rules (SPEC §6.3, CR-001 §5.11, D-1015, D-1032), in one place so a
// CMS change is one edit here. Every threshold is a parameter: nothing below hard-codes 75, 20 or 73.85.
const format_js_1 = require("./format.js");
exports.SCORING_RULESETS = ["APP", "MIPS_TRADITIONAL"];
exports.PARTIAL_POINTS_METHODS = ["CMS_LINEAR", "INTERPOLATE", "WHOLE_DECILE"];
exports.CASE_MINIMUM_BASES = ["ELIGIBLE_DENOMINATOR", "PERFORMANCE_DENOMINATOR"];
exports.COMPLETENESS_BASES = ["DISPLAY", "CMS"];
exports.SCORING_STATUSES = [
    "NO_DATA",
    "BELOW_COMPLETENESS",
    "NO_BENCHMARK",
    "BELOW_CASE_MINIMUM",
    "SCORED",
];
const percent = (part, whole) => whole === 0 ? null : (0, format_js_1.roundHalfUp)((100 * part) / whole, 2);
/** The three rates, each rounded half-up to 2 decimals before any benchmark comparison (DD-17). */
function rates(counts) {
    const complete = counts.met + counts.notMet + counts.exceptions;
    return {
        reportingRate: percent(complete, counts.totalInstances),
        cmsDataCompleteness: percent(complete + counts.excluded, counts.totalInstances + counts.excluded),
        performanceRate: percent(counts.met, counts.met + counts.notMet),
    };
}
/** The completeness scoring compares with the threshold: the reporting rate under `DISPLAY`, CMS's otherwise (D-1015). */
function dataCompleteness(r, basis) {
    return basis === "DISPLAY" ? r.reportingRate : r.cmsDataCompleteness;
}
/** The bound of a decile on its worse side: the low end for a normal measure, the high end for an inverse one. */
function worseEnd(range, direction) {
    return direction === "normal" ? range.low : range.high;
}
/** Whether `rate` is at or better than `bound`: higher is better for a normal measure, lower for an inverse one. */
function atOrBetter(rate, bound, direction) {
    return direction === "normal" ? rate >= bound : rate <= bound;
}
/**
 * The decile a rate falls in: the best decile whose worse end the rate reaches. A rate in a rounding gap
 * between two ranges, or above a range followed by `--` deciles, stays in the lower decile. NULL when the
 * rate is worse than decile 1's worse end.
 */
function findDecile(rate, benchmark) {
    const reached = benchmark.deciles.filter((d) => atOrBetter(rate, worseEnd(d, benchmark.direction), benchmark.direction));
    return reached.reduce((best, d) => (best === null || d.decile > best.decile ? d : best), null);
}
// Floor of fraction × 9 with a small tolerance, so 0.7 × 9 = 6.3 is not read as 6.299999… (SPEC §6.3).
const tenthsOf = (fraction) => Math.min(Math.floor(fraction * 9 + 1e-9), 9) / 10;
/** SPEC §6.3 `INTERPOLATE`: d + floor(fraction × 9) ÷ 10 within decile d's own range; zero width scores d.0. */
function interpolate(rate, range, direction) {
    const width = range.high - range.low;
    if (width === 0)
        return range.decile;
    const fraction = direction === "normal" ? (rate - range.low) / width : (range.high - rate) / width;
    return range.decile + tenthsOf(fraction);
}
/**
 * CR-001 §5.11 `CMS_LINEAR`: k + (rate − L(k)) ÷ (L(k+1) − L(k)), where L is a decile's worse end and
 * k+1 the next defined decile (decile 10's worse end, or 100 / 0 when none is defined); rounded half-up
 * to `decimals` (1, or 2 for Screen 6's display, CR-003 CR3-011) and never above k.9 or k.99.
 */
function cmsLinear(rate, range, benchmark, decimals) {
    const next = benchmark.deciles
        .filter((d) => d.decile > range.decile)
        .reduce((first, d) => (first === null || d.decile < first.decile ? d : first), null);
    const edge = benchmark.direction === "normal" ? 100 : 0;
    const lower = worseEnd(range, benchmark.direction);
    const upper = next === null ? edge : worseEnd(next, benchmark.direction);
    const span = Math.abs(upper - lower);
    if (span === 0)
        return range.decile;
    const fraction = Math.abs(rate - lower) / span;
    const ceiling = (0, format_js_1.roundHalfUp)(range.decile + 1 - 10 ** -decimals, decimals);
    return Math.min((0, format_js_1.roundHalfUp)(range.decile + fraction, decimals), ceiling);
}
/**
 * Points for a rate that is being scored. Below decile 1's worse end scores 1.0; decile 10 scores 10.
 * The seven-point cap applies only under `MIPS_TRADITIONAL` (D-1015). `decimals` 2 is Screen 6's
 * display only (CR-003 CR3-011); nothing that stores or totals points passes it.
 */
function decilePoints(rate, benchmark, params) {
    const range = findDecile(rate, benchmark);
    let decile;
    let points;
    if (range === null) {
        decile = 1;
        points = 1;
    }
    else if (range.decile === 10) {
        decile = 10;
        points = 10;
    }
    else {
        decile = range.decile;
        points =
            params.partialPointsMethod === "WHOLE_DECILE"
                ? range.decile
                : params.partialPointsMethod === "INTERPOLATE"
                    ? interpolate(rate, range, benchmark.direction)
                    : cmsLinear(rate, range, benchmark, params.decimals ?? 1);
    }
    if (params.scoringRuleset === "MIPS_TRADITIONAL" && benchmark.sevenPointCap) {
        points = Math.min(points, params.toppedOutCap);
    }
    return { decile, points };
}
/** A measure's status and points: the first matching step of SPEC §6.3 or CR-001 §5.11 decides. */
function scoreMeasure(input, params) {
    const { counts, benchmark, isSmallPractice } = input;
    const app = params.scoringRuleset === "APP";
    const small = (value) => (isSmallPractice ? value : 0);
    const excluded = (status, legacyPoints) => app
        ? { status, points: null, decile: null, counted: false }
        : { status, points: legacyPoints, decile: null, counted: true };
    if (counts.totalInstances === 0)
        return { status: "NO_DATA", points: 0, decile: null, counted: true };
    const counted = rates(counts);
    const r = input.reportingRate === undefined
        ? counted
        : { ...counted, reportingRate: input.reportingRate };
    const completeness = dataCompleteness(r, params.completenessBasis);
    if (completeness === null || completeness < params.dataCompletenessThreshold) {
        const points = app ? 0 : small(params.smallPracticePointsLowCompleteness);
        return {
            status: "BELOW_COMPLETENESS",
            points,
            decile: null,
            counted: true,
        };
    }
    if (benchmark === null)
        return excluded("NO_BENCHMARK", small(params.smallPracticePointsNoBenchmark));
    const caseCount = params.caseMinimumBasis === "ELIGIBLE_DENOMINATOR"
        ? counts.totalInstances
        : counts.met + counts.notMet;
    if (caseCount < params.caseMinimum || r.performanceRate === null) {
        return excluded("BELOW_CASE_MINIMUM", small(params.smallPracticePointsNoBenchmark));
    }
    const { decile, points } = decilePoints(r.performanceRate, benchmark, params);
    return { status: "SCORED", points, decile, counted: true };
}
/** Quality earned over 10 × the measures that count, and Score Est. as that ratio in percent (CR-001 §5.11). */
function qualityProjection(scores) {
    const counted = scores.filter((s) => s.counted);
    const earned = (0, format_js_1.roundHalfUp)(counted.reduce((sum, s) => sum + (s.points ?? 0), 0), 1);
    const max = 10 * counted.length;
    return {
        earned,
        max,
        scoreEst: max === 0 ? null : (0, format_js_1.roundHalfUp)((100 * earned) / max, 2),
    };
}
/**
 * The reporting incentive's percentiles (CR-001 §5.11, Calculation Specification S7; the J-039 acceptance
 * line): an outcome measure at or better than the 10th, and a different measure at or better than the 40th.
 */
exports.INCENTIVE_PERCENTILES = { outcome: 10, other: 40 };
/** DD-19 (approved 2026-09-25, D-1033): decile k begins at the ((k − 1) × 10)th percentile. */
function decileAtPercentile(percentile) {
    return Math.floor(percentile / 10) + 1;
}
/**
 * The rate a measure must reach to be in decile `k` or better: the worse end of the lowest defined decile
 * at or above `k`, so a `--` decile hands its boundary to the next defined one; null when none is defined.
 * `reachesDecile` passes exactly the rates at or better than it, since defined deciles are ordered.
 */
function decileBoundary(benchmark, k) {
    const first = benchmark.deciles
        .filter((d) => d.decile >= k)
        .reduce((lowest, d) => (lowest === null || d.decile < lowest.decile ? d : lowest), null);
    return first === null ? null : worseEnd(first, benchmark.direction);
}
/**
 * Whether a measure's rate reaches decile `k` or better. Through `findDecile`, so a `--` decile left out
 * of the benchmark is passed by any rate that reaches a defined decile above it.
 */
function reachesDecile(m, k) {
    if (!m.benchmark || m.performanceRate === null)
        return false;
    const reached = findDecile(m.performanceRate, m.benchmark);
    return reached !== null && reached.decile >= k;
}
/**
 * The APP reporting incentive (CR-001 §5.11, Calculation Specification S7): every measure at or above
 * the completeness threshold; an outcome measure at or better than its `outcomeDecile` boundary; and a
 * different measure at or better than its `otherDecile` boundary. Completeness short is `NOT_MET`; a
 * percentile short is `NOT_MET_FROM_SUBMITTED_MEASURES`, because CMS still adds the claims measures.
 */
function reportingIncentive(measures, params) {
    const allMeetCompleteness = measures.every((m) => m.completeness !== null && m.completeness >= params.completenessThreshold);
    const outcomes = measures.filter((m) => params.outcomeMeasureKeys.includes(m.measureKey) && reachesDecile(m, params.outcomeDecile));
    const outcomeAtDecile = outcomes.length > 0;
    // A measure at the other boundary counts when some outcome measure other than itself reaches the outcome
    // boundary; with none reaching it, the item still shows whether one measure reaches the other boundary.
    const otherAtDecile = measures.some((m) => reachesDecile(m, params.otherDecile) &&
        (outcomes.length === 0 || outcomes.some((o) => o.measureKey !== m.measureKey)));
    const status = !allMeetCompleteness
        ? "NOT_MET"
        : outcomeAtDecile && otherAtDecile
            ? "MET"
            : "NOT_MET_FROM_SUBMITTED_MEASURES";
    return {
        status,
        allMeetCompleteness,
        outcomeAtDecile,
        otherAtDecile,
        awaitingBenchmark: measures.filter((m) => m.benchmark === null).map((m) => m.measureKey),
    };
}
/**
 * The Shared Savings Program quality floor of 42 CFR 425.512(a)(7)(ii) (D-1032): a status beside the
 * projection that never changes it. Any measure without data or below completeness → `NOT_ELIGIBLE`;
 * otherwise any measure without a benchmark → `FLOOR_IF_NO_BENCHMARK`; otherwise `NOT_TRIGGERED`.
 */
function standardFloor(measures, floorValue) {
    const withoutBenchmark = measures
        .filter((m) => m.status === "NO_BENCHMARK")
        .map((m) => m.measureKey);
    const ineligible = measures.some((m) => m.status === "NO_DATA" || m.status === "BELOW_COMPLETENESS");
    const status = ineligible
        ? "NOT_ELIGIBLE"
        : withoutBenchmark.length > 0
            ? "FLOOR_IF_NO_BENCHMARK"
            : "NOT_TRIGGERED";
    return {
        status,
        value: floorValue,
        measuresWithoutBenchmark: withoutBenchmark,
    };
}
//# sourceMappingURL=scoring.js.map